import { Component, effect, inject } from '@angular/core';
import { PickListModule } from 'primeng/picklist';
import { PrimeTemplate } from 'primeng/api';
import { GlobalsStore } from '../../../stores/globals.state';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { FormBuilder } from '@angular/forms';
import { AtlasConfiguration, Commune } from '../../../models/commune';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { DividerModule } from 'primeng/divider';
import { HeaderComponent } from '../../atoms/header/header.component';

@Component({
  selector: 'app-configuration-navigation',
  standalone: true,
  imports: [PickListModule, PrimeTemplate, DividerModule, HeaderComponent],
  templateUrl: './configuration-navigation.component.html',
  styleUrl: './configuration-navigation.component.scss',
})
export class ConfigurationNavigationComponent {
  readonly globalStore = inject(GlobalsStore);
  readonly communeStore = inject(CommuneStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);

  activeCommune: Commune | null = null;
  atlasConfiguration?: AtlasConfiguration;
  initialized = false;
  selection: BookmarkCategory[] = [];
  availableItems: BookmarkCategory[] = [];

  constructor(private fb: FormBuilder) {
    effect(() => {
      if (!this.activeCommune) {
        this.activeCommune = this.globalStore.activeCommune();
        this.atlasConfiguration = this.activeCommune?.atlasConfig;
      }
      this.init();
    });
  }

  /**
   * Initializes the component by setting up the selection and available items.
   * This method is typically called to prepare the component for use.
   * @return {void} Does not return any value.
   */
  init(): void {
    this.setSelection();
    this.setAvailableItems();
  }

  /**
   * Updates the selection property based on the active commune's atlas configuration.
   * If no selection data is available, it sets the selection to an empty array.
   * Otherwise, it maps over the selection IDs to fetch corresponding bookmark categories
   * and filters out any invalid or undefined categories.
   *
   * @return {void} This method does not return a value.
   */
  setSelection(): void {
    const selection = this.activeCommune?.atlasConfig?.navigation;
    if (!selection) {
      this.selection = [];
      return;
    }
    this.selection = selection
      .map(_id => {
        return this.bookmarkCategoryStore.getById(_id);
      })
      .filter(bookmarkCategory => !!bookmarkCategory);
  }

  /**
   * Updates the availableItems array by filtering out items that are already selected.
   * It ensures that only items not present in the current selection are included.
   * @return {void} This method does not return a value.
   */
  setAvailableItems(): void {
    const selectedIds = this.selection.map(selected => selected._id);
    this.availableItems = this.bookmarkCategoryStore
      .asSortedArray()
      .filter(item => !selectedIds.includes(item._id));
  }

  /**
   * Saves the current selection into the `atlasConfig` navigation property of the active commune.
   * If no active commune is present, the method exits without performing any operations.
   * It updates the active commune's atlas configuration with the IDs of the selected items
   * and persists the changes to the `communeStore`.
   *
   * @return {void} Does not return a value.
   */
  saveToAtlasConfig(): void {
    if (!this.activeCommune) return;
    console.log(this.selection);
    this.activeCommune.atlasConfig.navigation = this.selection.map(
      item => item._id
    );
    this.communeStore.update(this.activeCommune);
  }
}
